import {
  FEATURE_TOGGLE_ONBOARDING_WORKFLOWS,
  FEATURE_TOGGLE_REDIRECT_TO_SPLIT_DOMAINS,
} from 'config/featureToggles'
import { NotificationManager } from 'helpers/NotificationsManager'
import {
  ITrackingUserValues,
  LOGIN_METHOD,
  TrackingService,
} from 'helpers/TrackingService'
import AuthService from 'helpers/authService'
import {
  redirectUserToSplitDomains,
  trackLoginSuccessEvent,
} from 'helpers/helperFuncs'
import history from 'helpers/history'
import {
  IAuthLogin,
  IAuthPayload,
  IGetMyUserIncludeParams,
  IGetMyUserParams,
  IMagicLogin,
  USER_ROLE,
} from 'models/Auth'
import { IChangePasswordParams } from 'models/Password'
import { IPutUserParams, IRecentlyLoggedInUser, IUser } from 'models/User'
import moment from 'moment-timezone'
import { API } from 'network'
import { Dispatch } from 'redux'
import { defaultRedirectRoutePerRole } from 'routes/routePermit'
import {
  isUserAdmin,
  shouldUseLoginCheck,
  userResponseMapper,
} from 'store/helpers'
import { handleErrorNotification } from '../../../helpers/ErrorHandlingService/ErrorHandlingService'
import {
  GetOpenUser as GetOpenUserAction,
  GetUserErrors as GetUserErrorsAction,
  GetUserImageLoader as GetUserImageLoaderAction,
  ResetAuthReducer as ResetAuthReducerAction,
  UserFetching as UserFetchingAction,
  UserHandleInfo as UserHandleInfoAction,
  UserLogin as UserLoginAction,
  UserLogout as UserLogoutAction,
} from './actions'
import { isUserUpshifter } from 'helpers/permissionsHelpers/roleHelpers'
import ROUTES from 'routes/routes'
import { Events } from 'helpers/TrackingService/events'
import { CODE_481_ACTIONS, STATUS_CODES } from 'network/config/error'
import { ILoginLocationState } from 'pages/public/Login/Login/PasswordLoginForm'

export type AuthActions =
  | ReturnType<typeof UserLogoutAction>
  | ReturnType<typeof GetUserErrorsAction>
  | ReturnType<typeof UserFetchingAction>
  | ReturnType<typeof UserLoginAction>
  | ReturnType<typeof GetOpenUserAction>
  | ReturnType<typeof UserHandleInfoAction>
  | ReturnType<typeof GetUserImageLoaderAction>
  | ReturnType<typeof ResetAuthReducerAction>

export const logoutUser = () => async (dispatch: Dispatch<AuthActions>) => {
  try {
    await API.Auth.logout()
    NotificationManager.success('User logged out successfully.')
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    NotificationManager.error('A problem occurred while logging out the user.')
    throw Error(error)
  } finally {
    TrackingService.reset()
    const recentlyLoggedInUser = AuthService.getRecentlyLoggedInUser()
    AuthService.clearStorage()
    if (recentlyLoggedInUser) {
      AuthService.setRecentlyLoggedInUser(recentlyLoggedInUser)
    }

    dispatch(UserLogoutAction())
    history.push(ROUTES.LOGIN)
  }
}

export const loginUser = (
  params: IAuthLogin,
  loginLocationState: ILoginLocationState,
) => async (dispatch: Dispatch<AuthActions>) => {
  TrackingService.track({ event: Events.LoginInitiated })
  const newParams = {
    ...params,
    timezone: moment.tz.guess(),
    include: ['incomplete_workflows', 'last_revoked_strike'],
  }
  dispatch(UserFetchingAction(true))
  try {
    const response = await API.Auth.login(newParams)
    const userData = (response.data as unknown) as IUser
    const role = userData.roles[0].name
    // Track only if user is upshifter
    if (isUserUpshifter(role)) {
      const trackingUserValues: ITrackingUserValues = {
        id: userData.id,
        email: userData.email,
        region: userData.regions[0].name,
      }
      trackLoginSuccessEvent(LOGIN_METHOD.PASSWORD_LOGIN, trackingUserValues)
    }
    if (isUserAdmin(role)) {
      handleAdminLogin(dispatch)
    } else if (shouldUseLoginCheck(userData)) {
      handleUserLogin(response, dispatch, role, loginLocationState)
    }
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    if (error.code !== STATUS_CODES.INVALID_CREDENTIALS) {
      NotificationManager.error(error.message)
    }
    throw error
  }
}

export const magicLoginUser = (params: IMagicLogin) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  TrackingService.track({ event: Events.LoginInitiated })
  const newParams = {
    ...params,
    timezone: moment.tz.guess(),
  }
  dispatch(UserFetchingAction(true))
  try {
    const response = await API.Auth.magicLogin(newParams)
    const userData = (response.data as unknown) as IUser
    const trackingUserValues: ITrackingUserValues = {
      id: userData.id,
      email: userData.email,
      region: userData.regions[0].name,
    }
    trackLoginSuccessEvent(LOGIN_METHOD.MAGIC_LINK, trackingUserValues)
    const role = userData.roles[0].name
    if (isUserAdmin(role)) {
      handleAdminLogin(dispatch)
    } else if (shouldUseLoginCheck(userData)) {
      AuthService.setBearerToken(response)
      const loggedInUser = userResponseMapper(response)
      if (loggedInUser) {
        AuthService.setLoggedInUser(loggedInUser)
      }
      dispatch(UserLoginAction())
      dispatch(GetOpenUserAction(loggedInUser))

      if (history.location.state && (history as any).location.state.from) {
        const { pathname, search } = (history as any).location.state.from
        // If there are any search params defined, include them in the request as well
        history.push(pathname + (search ? search : ''))
      }
      // Otherwise -> redirect to the default '/shifts' location
      else {
        history.push(defaultRedirectRoutePerRole[role])
      }
    }
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    NotificationManager.error('A problem occurred while logging in the user.')
    throw Error(error)
  }
}

export const forgotPassword = (email: string) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  dispatch(UserFetchingAction(true))
  try {
    const response = await API.Password.forgotPassword({ email })
    dispatch(UserFetchingAction(false))
    NotificationManager.success('Check your email for reset link!')
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    handleErrorNotification(error)
    throw Error(error)
  }
}

export const changePassword = (params: IChangePasswordParams) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  try {
    await API.Password.changePassword(params)
    NotificationManager.success('Password Updated!')
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    handleErrorNotification(error)
    throw Error(error)
  }
}

export const getMyUser = (params?: IGetMyUserParams) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  dispatch(UserFetchingAction(true))
  try {
    const response = await API.Auth.me(params)
    const loggedInUser = userResponseMapper(response)
    if (loggedInUser) {
      AuthService.setLoggedInUser(loggedInUser)
    }
    dispatch(GetOpenUserAction(loggedInUser))
    return response
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    handleErrorNotification(error)
    throw Error(error)
  } finally {
    dispatch(UserFetchingAction(false))
  }
}

export const changeUserInfo = (params: IPutUserParams, id: number) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  dispatch(UserFetchingAction(true))
  try {
    const response = await API.Users.putUser(id, params)
    const loggedInUser = userResponseMapper(response)
    if (loggedInUser) {
      AuthService.setLoggedInUser(loggedInUser)
    }
    dispatch(GetOpenUserAction(loggedInUser))
    if (!params.new_app) {
      NotificationManager.info('You changed profile info!')
    }
  } catch (error) {
    if (error.action !== CODE_481_ACTIONS.FLAGGED_WORDS) {
      dispatch(
        GetUserErrorsAction({
          code: error.code,
          message: error.message,
        }),
      )
      handleErrorNotification(error)
    }
    throw error
  } finally {
    dispatch(UserFetchingAction(false))
  }
}

export const getUserImage = (params: FormData, id: number) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  dispatch(GetUserImageLoaderAction(true))
  try {
    const response = await API.Users.updateUserAvatar(id, params)
    const loggedInUser = userResponseMapper(response)
    if (loggedInUser) {
      AuthService.setLoggedInUser(loggedInUser)
      const recentlyLoggedInUser: IRecentlyLoggedInUser = {
        first_name: loggedInUser.first_name,
        avatar_thumb_url: loggedInUser.avatar_thumb_url,
        email: loggedInUser.email,
      }
      AuthService.setRecentlyLoggedInUser(recentlyLoggedInUser)
    }
    dispatch(GetOpenUserAction(loggedInUser))
    NotificationManager.info('You changed profile info!')
    return response
  } catch (error) {
    if (error.action !== CODE_481_ACTIONS.FACE_ANALYSIS_ERROR) {
      dispatch(
        GetUserErrorsAction({
          code: error.code,
          message: error.message,
        }),
      )
      handleErrorNotification(error)
    }
    throw error
  } finally {
    dispatch(GetUserImageLoaderAction(false))
  }
}

/**
 * Function that handles admins that try to enter the application
 * @param dispatch Redux dispatch function
 */
const handleAdminLogin = (dispatch: Dispatch<AuthActions>) => {
  const type = 'warning'
  const message =
    'We have opened the new app only to our Upshifters and Business'
  dispatch(
    GetUserErrorsAction({
      code: STATUS_CODES.INVALID_CREDENTIALS,
      message,
    }),
  )
  dispatch(UserHandleInfoAction({ message, type }))
  dispatch(UserFetchingAction(false))
  history.push(ROUTES.LOGIN)
}

/**
 * Function that handles users logging into the app
 * @param response Response received from the API
 * @param dispatch Redux dispatch function
 * @param role Role of the logged in user
 */
const handleUserLogin = (
  response: IAuthPayload,
  dispatch: Dispatch<AuthActions>,
  role: USER_ROLE,
  loginLocationState: ILoginLocationState,
) => {
  const loggedInUser = userResponseMapper(response)

  // If the logged in upshifter has incomplete workflows, set the local storage item to true
  const isUserUpshifter =
    role === USER_ROLE.UPSHIFTER || role === USER_ROLE.LABOR
  if (
    isUserUpshifter &&
    FEATURE_TOGGLE_ONBOARDING_WORKFLOWS &&
    loggedInUser.incomplete_workflows
  ) {
    localStorage.setItem('redirectToIncompleteWorkflowsPage', 'true')
  }

  if (FEATURE_TOGGLE_REDIRECT_TO_SPLIT_DOMAINS) {
    // If the feature toggle to redirect the users to the split domains is enabled
    // we're checking if the loggedInUser is on the correct domain
    // if the user's on the correct domain, the function will return false
    // and the login flow will continue
    // if the user's on the incorrect domain, the function will return true,
    // the thunk will terminate and the user will be redirected to the correct domain.
    const shouldTerminateThunk = redirectUserToSplitDomains(
      loggedInUser.role,
      response.meta.auth.token,
      dispatch,
    )
    if (shouldTerminateThunk) {
      return
    }
  }
  AuthService.setBearerToken(response)

  if (loggedInUser) {
    AuthService.setLoggedInUser(loggedInUser)
    const recentlyLoggedInUser: IRecentlyLoggedInUser = {
      first_name: loggedInUser.first_name,
      avatar_thumb_url: loggedInUser.avatar_thumb_url,
      email: loggedInUser.email,
    }
    AuthService.setRecentlyLoggedInUser(recentlyLoggedInUser)
  }

  dispatch(UserLoginAction())
  dispatch(GetOpenUserAction(loggedInUser))

  if (loginLocationState) {
    const { pathname, search } = loginLocationState.from

    // If there are any search params defined, include them in the request as well
    history.push(pathname + (search ? search : ''))
  }

  // Otherwise -> redirect to the default '/shifts' location
  else {
    history.push(defaultRedirectRoutePerRole[role])
  }
}

export const signHandbook = (params?: IGetMyUserIncludeParams) => async (
  dispatch: Dispatch<AuthActions>,
) => {
  try {
    const response = await API.Users.postSignHandbook(params)
    const loggedInUser = userResponseMapper(response)
    if (loggedInUser) {
      AuthService.setLoggedInUser(loggedInUser)
    }
    dispatch(GetOpenUserAction(loggedInUser))
    return response
  } catch (error) {
    dispatch(
      GetUserErrorsAction({
        code: error.code,
        message: error.message,
      }),
    )
    handleErrorNotification(error)
    throw Error(error)
  }
}
